// Email Notifications Service using EmailJS
// Sends real emails directly from the browser - no backend needed
// Config is stored in Supabase (shared across all users) with localStorage cache

import emailjs from '@emailjs/browser';
import { logger } from './logger';
import { supabase } from './supabase';
export interface EmailConfig {
  publicKey: string;
  serviceId: string;
  templateId: string;
  enabled: boolean;
  senderName: string;
}
export interface AppointmentEmailData {
  patientName: string;
  patientEmail: string;
  doctorName: string;
  serviceName: string;
  date: string;
  time: string;
  duration: number;
  notes?: string;
  cancelReason?: string;
  oldDate?: string;
  oldTime?: string;
}
const STORAGE_KEY = 'savidna-email-config';
const SUPABASE_KEY = 'email_config';
class EmailNotificationService {
  private config: EmailConfig | null = null;
  private initialized = false;
  private supabaseLoaded = false;
  constructor() {
    // Load from localStorage cache immediately (fast)
    this.loadFromLocalStorage();
    // Then async load from Supabase (shared/authoritative)
    this.loadFromSupabase();
  }
  private loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Force enabled to true - email is always active when configured
        this.config = {
          ...parsed,
          enabled: true
        };
        if (this.config?.publicKey && !this.initialized) {
          emailjs.init(this.config.publicKey);
          this.initialized = true;
        }
      }
    } catch (e) {
      logger.error('Error loading email config from localStorage:', e);
    }
  }
  async loadFromSupabase(): Promise<void> {
    try {
      const {
        data,
        error
      } = await supabase.from('app_settings').select('value').eq('key', SUPABASE_KEY).single();
      if (!error && data?.value) {
        const supabaseConfig = data.value as EmailConfig;
        // Force enabled to true - always active
        supabaseConfig.enabled = true;
        this.config = supabaseConfig;
        // Cache to localStorage for fast loading next time
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config));
        // Initialize EmailJS
        if (this.config.publicKey && !this.initialized) {
          emailjs.init(this.config.publicKey);
          this.initialized = true;
        } else if (this.config.publicKey && this.initialized) {
          // Re-init if key changed
          emailjs.init(this.config.publicKey);
        }
        this.supabaseLoaded = true;
        logger.log('✅ Email config cargada desde Supabase (compartida)');
      } else if (error && error.code !== 'PGRST116') {
        // PGRST116 = row not found, which is fine for first time
        logger.warn('⚠️ No se pudo cargar email config desde Supabase:', error.message);
        logger.warn('   Ejecuta supabase-migration-app-settings.sql para habilitar config compartida');
      }
    } catch (err) {
      logger.warn('⚠️ Supabase no disponible para email config, usando localStorage');
    }
  }
  async saveConfig(config: EmailConfig): Promise<{
    success: boolean;
    error?: string;
  }> {
    // Force enabled to true - always active
    const configToSave = {
      ...config,
      enabled: true
    };
    this.config = configToSave;

    // Save to localStorage (cache)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configToSave));

    // Re-initialize EmailJS with new public key
    if (configToSave.publicKey) {
      emailjs.init(configToSave.publicKey);
      this.initialized = true;
    }

    // Save to Supabase (shared across all users)
    try {
      const {
        error
      } = await supabase.from('app_settings').upsert({
        key: SUPABASE_KEY,
        value: configToSave,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'key'
      });
      if (error) {
        logger.error('❌ Error guardando email config en Supabase:', error);
        // If table doesn't exist, still works via localStorage
        if (error.code === '42P01') {
          return {
            success: true,
            error: 'Guardado localmente. Ejecuta supabase-migration-app-settings.sql para compartir con todos los usuarios.'
          };
        }
        return {
          success: true,
          error: `Guardado localmente. Error Supabase: ${error.message}`
        };
      }
      logger.log('✅ Email config guardada en Supabase (compartida con todos los usuarios)');
      return {
        success: true
      };
    } catch (err) {
      logger.warn('⚠️ No se pudo guardar en Supabase, guardado solo en localStorage');
      return {
        success: true,
        error: 'Guardado localmente. Supabase no disponible.'
      };
    }
  }
  getConfig(): EmailConfig | null {
    return this.config;
  }
  isEnabled(): boolean {
    // Always enabled if credentials are complete
    return !!(this.config?.publicKey && this.config?.serviceId && this.config?.templateId);
  }
  isSupabaseLoaded(): boolean {
    return this.supabaseLoaded;
  }
  private async sendEmail(templateParams: Record<string, string>): Promise<{
    success: boolean;
    error?: string;
  }> {
    // If not loaded from Supabase yet, try loading now
    if (!this.supabaseLoaded && !this.config?.publicKey) {
      await this.loadFromSupabase();
    }
    if (!this.config?.publicKey || !this.config?.serviceId || !this.config?.templateId) {
      return {
        success: false,
        error: 'EmailJS no está configurado completamente'
      };
    }
    if (!this.initialized) {
      emailjs.init(this.config.publicKey);
      this.initialized = true;
    }
    try {
      const response = await emailjs.send(this.config.serviceId, this.config.templateId, templateParams);
      if (response.status === 200) {
        logger.log('✅ Email enviado exitosamente:', response.text);
        return {
          success: true
        };
      } else {
        return {
          success: false,
          error: `Error: ${response.text}`
        };
      }
    } catch (error: any) {
      logger.error('❌ Error enviando email:', error);
      const message = error?.text || error?.message || 'Error desconocido';
      return {
        success: false,
        error: message
      };
    }
  }
  async sendAppointmentCreated(data: AppointmentEmailData): Promise<{
    success: boolean;
    error?: string;
  }> {
    if (!this.isEnabled()) {
      return {
        success: false,
        error: 'Notificaciones por correo no configuradas'
      };
    }
    return this.sendEmail({
      to_email: data.patientEmail,
      to_name: data.patientName,
      from_name: this.config?.senderName || 'Savidna',
      subject: `✅ Cita Confirmada - ${this.config?.senderName || 'Savidna'}`,
      patient_name: data.patientName,
      doctor_name: `Dr. ${data.doctorName}`,
      service_name: data.serviceName,
      date: data.date,
      time: data.time,
      duration: `${data.duration} minutos`,
      notes: data.notes || 'Sin notas',
      action_type: 'Cita Confirmada',
      action_color: '#10b981',
      message: `Hola ${data.patientName},\n\nTu cita ha sido agendada exitosamente.\n\n📅 Fecha: ${data.date}\n🕐 Hora: ${data.time}\n👨‍⚕️ Doctor: Dr. ${data.doctorName}\n🏥 Servicio: ${data.serviceName}\n⏱️ Duración: ${data.duration} minutos${data.notes ? '\n📝 Notas: ' + data.notes : ''}\n\n💡 Recordatorio: Por favor llega 10 minutos antes.\n\nSaludos,\n${this.config?.senderName || 'Savidna'}`
    });
  }
  async sendAppointmentUpdated(data: AppointmentEmailData): Promise<{
    success: boolean;
    error?: string;
  }> {
    if (!this.isEnabled()) {
      return {
        success: false,
        error: 'Notificaciones por correo no configuradas'
      };
    }
    return this.sendEmail({
      to_email: data.patientEmail,
      to_name: data.patientName,
      from_name: this.config?.senderName || 'Savidna',
      subject: `📝 Cita Actualizada - ${this.config?.senderName || 'Savidna'}`,
      patient_name: data.patientName,
      doctor_name: `Dr. ${data.doctorName}`,
      service_name: data.serviceName,
      date: data.date,
      time: data.time,
      duration: `${data.duration} minutos`,
      notes: data.notes || 'Sin notas',
      action_type: 'Cita Actualizada',
      action_color: '#3b82f6',
      message: `Hola ${data.patientName},\n\nLos detalles de tu cita han sido actualizados.\n\n📅 Fecha: ${data.date}\n🕐 Hora: ${data.time}\n👨‍⚕️ Doctor: Dr. ${data.doctorName}\n🏥 Servicio: ${data.serviceName}\n\nSaludos,\n${this.config?.senderName || 'Savidna'}`
    });
  }
  async sendAppointmentCancelled(data: AppointmentEmailData): Promise<{
    success: boolean;
    error?: string;
  }> {
    if (!this.isEnabled()) {
      return {
        success: false,
        error: 'Notificaciones por correo no configuradas'
      };
    }
    return this.sendEmail({
      to_email: data.patientEmail,
      to_name: data.patientName,
      from_name: this.config?.senderName || 'Savidna',
      subject: `❌ Cita Cancelada - ${this.config?.senderName || 'Savidna'}`,
      patient_name: data.patientName,
      doctor_name: `Dr. ${data.doctorName}`,
      service_name: data.serviceName,
      date: data.date,
      time: data.time,
      duration: `${data.duration} minutos`,
      notes: data.cancelReason || 'No especificado',
      action_type: 'Cita Cancelada',
      action_color: '#ef4444',
      message: `Hola ${data.patientName},\n\nTu cita ha sido cancelada.\n\n📅 Fecha: ${data.date}\n🕐 Hora: ${data.time}\n👨‍⚕️ Doctor: Dr. ${data.doctorName}\n📋 Motivo: ${data.cancelReason || 'No especificado'}\n\nSi deseas reagendar, contáctanos.\n\nSaludos,\n${this.config?.senderName || 'Savidna'}`
    });
  }
  async sendAppointmentRescheduled(data: AppointmentEmailData): Promise<{
    success: boolean;
    error?: string;
  }> {
    if (!this.isEnabled()) {
      return {
        success: false,
        error: 'Notificaciones por correo no configuradas'
      };
    }
    return this.sendEmail({
      to_email: data.patientEmail,
      to_name: data.patientName,
      from_name: this.config?.senderName || 'Savidna',
      subject: `🔄 Cita Reprogramada - ${this.config?.senderName || 'Savidna'}`,
      patient_name: data.patientName,
      doctor_name: `Dr. ${data.doctorName}`,
      service_name: data.serviceName,
      date: data.date,
      time: data.time,
      duration: `${data.duration} minutos`,
      notes: data.oldDate && data.oldTime ? `Fecha anterior: ${data.oldDate} a las ${data.oldTime}` : '',
      action_type: 'Cita Reprogramada',
      action_color: '#f59e0b',
      message: `Hola ${data.patientName},\n\nTu cita ha sido reprogramada.\n\n${data.oldDate ? '📅 Fecha anterior: ' + data.oldDate + ' a las ' + data.oldTime + '\n\n' : ''}📅 Nueva Fecha: ${data.date}\n🕐 Nueva Hora: ${data.time}\n👨‍⚕️ Doctor: Dr. ${data.doctorName}\n🏥 Servicio: ${data.serviceName}\n\n💡 Recordatorio: Por favor llega 10 minutos antes.\n\nSaludos,\n${this.config?.senderName || 'Savidna'}`
    });
  }
  async sendAppointmentReminder(data: AppointmentEmailData): Promise<{
    success: boolean;
    error?: string;
  }> {
    if (!this.isEnabled()) {
      return {
        success: false,
        error: 'Notificaciones por correo no configuradas'
      };
    }
    return this.sendEmail({
      to_email: data.patientEmail,
      to_name: data.patientName,
      from_name: this.config?.senderName || 'Savidna',
      subject: `⏰ Recordatorio de Cita - Mañana - ${this.config?.senderName || 'Savidna'}`,
      patient_name: data.patientName,
      doctor_name: `Dr. ${data.doctorName}`,
      service_name: data.serviceName,
      date: data.date,
      time: data.time,
      duration: `${data.duration} minutos`,
      notes: '',
      action_type: 'Recordatorio de Cita',
      action_color: '#8b5cf6',
      message: `Hola ${data.patientName},\n\nTe recordamos que tienes una cita programada para mañana.\n\n📅 Fecha: ${data.date}\n🕐 Hora: ${data.time}\n👨‍⚕️ Doctor: Dr. ${data.doctorName}\n🏥 Servicio: ${data.serviceName}\n⏱️ Duración: ${data.duration} minutos\n\n💡 Por favor llega 10 minutos antes.\n\nSaludos,\n${this.config?.senderName || 'Savidna'}`
    });
  }
  async sendTestEmail(recipientEmail: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    if (!this.config?.publicKey || !this.config?.serviceId || !this.config?.templateId) {
      return {
        success: false,
        error: 'Configura todos los campos de EmailJS primero'
      };
    }
    return this.sendEmail({
      to_email: recipientEmail,
      to_name: 'Usuario de Prueba',
      from_name: this.config.senderName || 'Savidna',
      subject: `✅ Prueba de Correo - ${this.config.senderName || 'Savidna'}`,
      patient_name: 'Paciente de Prueba',
      doctor_name: 'Dr. Doctor de Prueba',
      service_name: 'Consulta de Prueba',
      date: new Date().toLocaleDateString('es-DO'),
      time: '10:00 AM',
      duration: '30 minutos',
      notes: '',
      action_type: 'Correo de Prueba',
      action_color: '#10b981',
      message: `⚡ Este es un correo de prueba enviado desde Savidna CRM.\n\nSi recibes este correo, ¡las notificaciones por EmailJS están funcionando correctamente!\n\nSaludos,\n${this.config.senderName || 'Savidna'}`
    });
  }
  async testConnection(): Promise<{
    success: boolean;
    error?: string;
  }> {
    if (!this.config?.publicKey || !this.config?.serviceId || !this.config?.templateId) {
      return {
        success: false,
        error: 'Faltan datos de configuración de EmailJS'
      };
    }
    return {
      success: true
    };
  }
}
export const emailNotificationService = new EmailNotificationService();